import { useState } from 'react';
import { fmtMoney, fmtHours, fmtNumber } from './format';
import { c, t } from '../../theme';

// ── The numbers behind the bars ──────────────────────────────────────────────
//
// Collapsed by default. The chart answers "did overtime move"; this answers
// "against what, exactly" -- every month the chart draws, with both years'
// figures, both run counts, and the reason a pair is not comparable when it
// isn't.
//
// IT IS HANDED THE CHART'S OWN POINTS AND NEVER FETCHES ITS OWN. That is the
// whole reason the filters are right here: the window, the lens/role/location
// filters and the cost-center selector are already baked into the array the
// chart drew, so this table cannot show a different cut than the bars above it.
// A second query with its own copy of the filter arguments is the shape of bug
// this panel spent a release fixing -- one dropped `&cc=` and the "source data"
// silently becomes the whole company under a heading naming one yard.
//
// WHAT IT DELIBERATELY DOES NOT SHOW. `ot_pct`, `total_hours` and
// `delta_ot_pct_points` all come back on these points and are all omitted. The
// payroll extract carries premium codes only and has never received a
// straight-time row, so the denominator does not exist and that family of
// numbers reads ~98% every month -- which is why OT % of hours was taken off
// the measure tabs. A table labelled "source data" is exactly where a removed
// metric gets resurrected on the grounds that it is only the raw figures, so
// it is named here as an omission rather than left to look like an oversight.

// Why a pair cannot be read straight across. Ordered by what the reader should
// do about it, not by how the API names it.
function noteFor(p) {
  if (p.partial) {
    const of = p.expected_periods ? ` of about ${p.expected_periods}` : '';
    return { text: `still running — ${p.periods} run${p.periods === 1 ? '' : 's'}${of} landed`, tone: 'warn' };
  }
  if (!p.prior.loaded) {
    return p.prior.before_history
      ? { text: 'earlier than our first payroll', tone: 'muted' }
      : { text: 'prior year not loaded', tone: 'warn' };
  }
  if (!p.yoy.comparable) {
    return { text: 'unequal runs — read per run', tone: 'warn' };
  }
  return null;
}

function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span style={s.muted}>—</span>;
  // Less overtime is the goal, so down is the good direction here.
  const tone = pct > 0 ? c.dangerText : pct < 0 ? c.successText : c.textMuted;
  return <span style={{ ...s.delta, color: tone }}>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>;
}

export default function YoySourceData({ points = [], measure = 'hours', scopeLabel = null }) {
  const [open, setOpen] = useState(false);

  if (!points.length) return null;

  const money = measure === 'amount';
  const fmt   = money ? fmtMoney : fmtHours;
  const cur   = (p) => (money ? p.ot_amount : p.ot_hours);
  const perRun = (p) => (money ? p.ot_amount_per_period : p.ot_hours_per_period);

  // Computed from the two per-run figures the API already returned, rather than
  // read from delta_hours_per_period_pct — that field exists for hours only, so
  // taking it for one measure and computing the other is how two columns that
  // should agree start disagreeing at the rounding.
  const runPct = (p) => {
    const a = perRun(p), b = perRun(p.prior);
    if (a === null || b === null || !b) return null;
    return ((a - b) / b) * 100;
  };

  const rows = points.slice().reverse();   // newest first: what gets read first
  const label = money ? 'OT cost' : 'OT hours';

  return (
    <div style={s.wrap}>
      <button type="button" style={s.toggle} onClick={() => setOpen((v) => !v)}
              aria-expanded={open} aria-controls="yoy-source-data">
        <span style={s.caret} aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span style={s.toggleText}>Source data</span>
        <span style={s.toggleHint}>
          {rows.length} month{rows.length === 1 ? '' : 's'} · {label}
          {scopeLabel ? ` · ${scopeLabel}` : ''}
        </span>
      </button>

      {open && (
        <div id="yoy-source-data" style={s.body}>
          <div style={s.sub}>
            The figures the chart above is drawn from, under the same filters. Per-run is the
            column to read where the two months hold a different number of payroll runs.
          </div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...s.th, ...s.thLeft }}>Month</th>
                  <th style={s.th}>{label}</th>
                  <th style={s.th}>Runs</th>
                  <th style={s.th}>Per run</th>
                  <th style={s.th}>Prior year</th>
                  <th style={s.th}>Runs</th>
                  <th style={s.th}>Per run</th>
                  <th style={s.th}>Change</th>
                  <th style={s.th} title="Per pay period — comparable even when the two months hold different numbers of payroll runs">
                    Per run
                  </th>
                  <th style={s.th}>Employees</th>
                  <th style={{ ...s.th, ...s.thLeft }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const note = noteFor(p);
                  const rp   = runPct(p);
                  return (
                    <tr key={p.month} style={p.partial ? s.trPartial : undefined}>
                      <td style={{ ...s.td, ...s.tdLeft }}>{p.label}</td>
                      <td style={s.td}>{fmt(cur(p))}</td>
                      <td style={s.td}>{fmtNumber(p.periods)}</td>
                      <td style={s.td}>
                        {perRun(p) === null ? <span style={s.muted}>—</span> : fmt(perRun(p))}
                      </td>
                      <td style={s.td}>
                        {p.prior.loaded ? fmt(cur(p.prior)) : <span style={s.muted}>—</span>}
                      </td>
                      <td style={s.td}>
                        {p.prior.loaded ? fmtNumber(p.prior.periods) : <span style={s.muted}>—</span>}
                      </td>
                      <td style={s.td}>
                        {p.prior.loaded && perRun(p.prior) !== null
                          ? fmt(perRun(p.prior))
                          : <span style={s.muted}>—</span>}
                      </td>
                      <td style={s.td}>
                        <Delta pct={money ? p.yoy.delta_amount_pct : p.yoy.delta_hours_pct} />
                      </td>
                      <td style={s.td}><Delta pct={rp} /></td>
                      <td style={s.td}>
                        {fmtNumber(p.employee_count)}
                        {p.prior.loaded && p.prior.employee_count !== null && (
                          <span style={s.was}> was {fmtNumber(p.prior.employee_count)}</span>
                        )}
                      </td>
                      <td style={{ ...s.td, ...s.tdLeft }}>
                        {note
                          ? <span style={note.tone === 'warn' ? s.noteWarn : s.muted}>{note.text}</span>
                          : <span style={s.muted}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '10px',
          overflow: 'hidden' },

  toggle: { display: 'flex', alignItems: 'baseline', gap: '8px', width: '100%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '11px 15px', textAlign: 'left' },
  caret:      { ...t.bodySm, color: c.textFaint, width: '10px' },
  toggleText: { ...t.bodySm, fontWeight: 600, color: c.text },
  toggleHint: { ...t.caption, color: c.textFaint },

  body: { borderTop: `1px solid ${c.border}` },
  sub:  { ...t.caption, color: c.textMuted, padding: '10px 15px 0', maxWidth: '78ch' },

  scroll: { overflowX: 'auto', padding: '8px 0 0' },
  table:  { width: '100%', borderCollapse: 'collapse' },

  th:     { ...t.label, color: c.textFaint, textAlign: 'right', padding: '9px 15px',
            borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
  thLeft: { textAlign: 'left' },

  td:     { ...t.bodySm, ...t.tabular, color: c.text, textAlign: 'right',
            padding: '8px 15px', borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
  tdLeft: { textAlign: 'left', fontVariantNumeric: 'normal' },

  trPartial: { background: c.surfaceSunk },

  delta:    { fontWeight: 600 },
  muted:    { color: c.textFaint },
  noteWarn: { color: c.warningText },
  was:      { ...t.micro, color: c.textFaint, marginLeft: '5px' },
};
